import { inject, Injectable } from '@angular/core';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { LocaleNumberPipe } from '@app/shared/pipes/locale-number/locale-number.pipe';
import { LocalePercentPipe } from '@app/shared/pipes/locale-number/locale-percent.pipe';
import { _, TranslateService } from '@ngx-translate/core';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { isNumber } from '@app/shared/utils/type-guards';
import { Length } from '@app/shared/units/length/length.model';
import { MetricLengthUnit } from '@app/shared/units/length/length-unit.enum';
import { measurementSystemLengthUnit_m_ft } from '@app/core/localize/measurement-system-length-unit';
import { IPosition } from '@app/core/interfaces/position.interface';
import { formatSerialNo } from '@app/shared/utils/string';
import { ICustomCoordinates } from '@app/api/interfaces/common/custom-coordinates';
import { CoordinateSystemsLookupService } from '@app/core/coordinate-systems/services/coordinate-systems-lookup.service';

@Injectable({
  providedIn: 'root',
})
export class FormattingDataService {
  protected readonly localeNumber = inject(LocaleNumberPipe);
  protected readonly localePercent = inject(LocalePercentPipe);
  protected readonly translateService = inject(TranslateService);
  protected readonly coordinateSystems = inject(CoordinateSystemsLookupService);
  private dayjsLocale = inject(DayjsLocaleService);

  get NO_DATA_VALUE_PLACEHOLDER_KEY() {
    return this.translateService.instant(_('@@shared-no-data-value-placeholder'));
  }
  public MORE_OR_EQUAL_SYMBOL = '≥';

  public getLocalizeMeasurementSystem() {
    return {
      [MeasurementSystem.Metric]: this.translateService.instant(_(`@@shared-measurement-system-metric__m`)),
      [MeasurementSystem.Imperial]: this.translateService.instant(_(`@@shared-measurement-system-imperial__ft`)),
    };
  }

  public formatDate(date: string | null | undefined) {
    return date ? this.dayjsLocale.format(date, DayJSDateFormat.MMMDYYYY) : this.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public formatDateTime(date: string | null | undefined) {
    return date ? this.dayjsLocale.format(date, DayJSDateFormat.MMMDDYYYYHHmm) : this.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public formatTime(date: string | null | undefined, format = DayJSDateFormat.HHmmss) {
    return date ? this.dayjsLocale.format(date, format) : this.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public formatPercent(value: number | null | undefined, minDecimals = 2, maxDecimals = 2): string {
    if (value === null || value === undefined) {
      return this.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }
    const safeValue = value ?? 0;
    return this.localePercent.transform(safeValue / 100, minDecimals, maxDecimals);
  }

  public formatNumber(value: number | null | undefined, minDecimals = 0, maxDecimals = 2): string {
    if (value === null || value === undefined) {
      return this.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }
    return this.localeNumber.transform(value, minDecimals, maxDecimals);
  }

  public formatDecibel(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return this.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }
    return this.translateService.instant(_('@@shared-value__decibel'), {
      I18N_VAR_VALUE: this.localeNumber.transform(value),
    });
  }

  public formatLength(
    value: number | undefined,
    viewMeasurementSystem: MeasurementSystem,
    moreOrEqual?: boolean
  ): string {
    const isPresent = isNumber(value);
    const convertedValue = isPresent
      ? new Length(value as number, MetricLengthUnit.Meter).round(
          measurementSystemLengthUnit_m_ft[viewMeasurementSystem],
          2
        )
      : null;

    return isPresent
      ? `${moreOrEqual ? this.MORE_OR_EQUAL_SYMBOL : ''} ${this.formatNumber(convertedValue, 0, 2)} ${this.getLocalizeMeasurementSystem()[viewMeasurementSystem]}`
      : this.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public formatCoordinates(value: IPosition | null | undefined): string {
    if (value === null || value === undefined) {
      return this.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }

    return this.translateService.instant(_('@@shared-value__coordinates'), {
      I18N_VAR_LATITUDE: this.localeNumber.transform(value.lat, 0, 6),
      I18N_VAR_LONGITUDE: this.localeNumber.transform(value.lon, 0, 6),
    });
  }

  public formatCustomCoordinates(value: ICustomCoordinates | null | undefined, divider = ' / '): string {
    const system = this.coordinateSystems.getFor(value);

    if (!value || !system) {
      return this.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }

    return [
      this.formatCoordinateAxis(system.axis1Label, value.axis1, system.axis1Unit),
      this.formatCoordinateAxis(system.axis2Label, value.axis2, system.axis2Unit),
    ].join(divider);
  }

  private formatCoordinateAxis(label: string, value: number, unit: string): string {
    return this.translateService.instant(
      unit ? _('@@shared-value__custom_coordinate_axis') : _('@@shared-value__custom_coordinate_axis_no_unit'),
      {
        I18N_VAR_LABEL: label,
        I18N_VAR_VALUE: this.localeNumber.transform(value, 0, 2),
        I18N_VAR_UNIT: unit,
      }
    );
  }

  public formatSerialNo(value?: string | undefined): string {
    return formatSerialNo(value) || this.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }
}
